// Page geometry: turn positional PDF text items into lines and cells.
//
// Kept free of pdfjs worker wiring and Vite-only imports so this can run
// outside the browser — the accuracy harness in tools/ exercises exactly this
// code rather than a reimplementation of it.

/** The shape this module needs from a pdfjs text item. */
export interface PositionedItem {
  transform: number[];
  width?: number;
  str: string;
}

export interface TextCell {
  x: number;
  y: number;
  width: number;
  text: string;
}

export interface TextLine {
  y: number;
  /** Runs of text separated by a gap wide enough to read as a column break. */
  cells: TextCell[];
  /** The line as a string, cells joined by a tab. */
  text: string;
}

export interface PageLayout {
  number: number;
  lines: TextLine[];
  text: string;
}

export interface ExtractedPdf {
  pageCount: number;
  pages: PageLayout[];
  rawText: string;
}

/** Items whose baselines are within this many points count as one line. */
const LINE_TOLERANCE = 2.5;
/** A horizontal gap this wide or wider reads as a column break, not a space. */
const COLUMN_GAP = 11;
/** Narrower than this and the gap is not even a space. */
const SPACE_GAP = 1.2;

export function buildLines(items: PositionedItem[]): TextLine[] {
  const placed = items
    .filter((it) => typeof it.str === 'string' && it.str.length > 0)
    .map((it) => {
      const [, , , , x, y] = it.transform;
      return { x, y, width: it.width ?? 0, text: it.str };
    })
    // Reading order: down the page, then left to right within a line.
    .sort((a, b) =>
      Math.abs(a.y - b.y) > LINE_TOLERANCE ? b.y - a.y : a.x - b.x,
    );

  const grouped: { y: number; parts: typeof placed }[] = [];
  for (const item of placed) {
    const last = grouped[grouped.length - 1];
    if (last && Math.abs(last.y - item.y) <= LINE_TOLERANCE) {
      last.parts.push(item);
    } else {
      grouped.push({ y: item.y, parts: [item] });
    }
  }

  return grouped.map(({ y, parts }) => {
    const cells: TextCell[] = [];
    let current: TextCell | null = null;
    let prevEnd: number | null = null;

    for (const part of parts) {
      const gap = prevEnd === null ? 0 : part.x - prevEnd;

      if (current === null || gap >= COLUMN_GAP) {
        // Column break: start a new cell.
        current = { x: part.x, y, width: part.width, text: part.text };
        cells.push(current);
      } else {
        // Same cell. pdfjs splits words at style changes (small caps split
        // "OVERVIEW" into "O" + "VERVIEW"), so only insert a space where the
        // geometry actually shows one.
        current.text += (gap >= SPACE_GAP ? ' ' : '') + part.text;
        current.width = part.x + part.width - current.x;
      }
      prevEnd = part.x + part.width;
    }

    for (const cell of cells) cell.text = cell.text.replace(/\s+$/, '');

    return {
      y,
      cells: cells.filter((c) => c.text.length > 0),
      text: cells
        .map((c) => c.text)
        .join('\t')
        .replace(/[ \t]+$/, ''),
    };
  });
}

/**
 * A page's plain text, as every consumer of a page must see it.
 *
 * The document pane highlights a span by character offset into this string, so
 * the exact bytes matter: the browser ingest and the tool that pre-extracts the
 * worked example's pages have to produce the same text from the same page or a
 * stored offset would point at the wrong words. One function, used by both, is
 * the only way to guarantee that.
 */
export function pageTextOf(lines: TextLine[]): string {
  return lines
    .map((l) => l.text)
    .join('\n')
    .replace(/\n{3,}/g, '\n\n');
}

/**
 * Lines that repeat on most pages are running headers and footers. They are
 * removed from the text used for section detection, because "Example M&V Plan"
 * on every page is not a heading and "2/07 page 5" is not content.
 */
export function findRunningLines(pages: PageLayout[]): Set<string> {
  if (pages.length < 4) return new Set();
  const counts = new Map<string, number>();

  for (const page of pages) {
    // Only the top and bottom three lines can be running furniture.
    const candidates = [...page.lines.slice(0, 3), ...page.lines.slice(-3)];
    for (const line of new Set(candidates.map((l) => l.text.trim()))) {
      if (!line || line.length > 90) continue;
      // Page numbers differ per page; normalise digits so they still group.
      counts.set(line.replace(/\d+/g, '#'), (counts.get(line.replace(/\d+/g, '#')) ?? 0) + 1);
    }
  }

  const threshold = Math.max(3, Math.floor(pages.length * 0.5));
  return new Set(
    [...counts.entries()].filter(([, n]) => n >= threshold).map(([k]) => k),
  );
}

export function isRunningLine(text: string, running: Set<string>): boolean {
  return running.has(text.trim().replace(/\d+/g, '#'));
}

